import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../hooks/useAuth';
import Layout from '../components/layout/Layout';
import SlidePanel from '../components/common/SlidePanel';
import Badge from '../components/common/Badge';
import { Input, Select, Textarea } from '../components/common/FormField';
import PatientSearchInput from '../components/patient/PatientSearchInput';
import FileShaClaimButton from '../components/common/FileShaClaimButton';
import api from '../services/api';
import { fmtDate, fmtDateTime } from '../utils/helpers';
import {useTranslation} from 'react-i18next';

export default function PalliativeCarePage() {
  const {t}=useTranslation();
  const { user } = useAuth();
  const [stats, setStats] = useState<any>({});
  const [patient, setPatient] = useState<any>(null);
  const [kase, setKase] = useState<any>(null);
  const [symptoms, setSymptoms] = useState<any[]>([]);
  const [plan, setPlan] = useState<any>(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState('');
  const notify = (m: string) => { setMsg(m); setTimeout(() => setMsg(''), 5000); };

  const [showCase, setShowCase] = useState(false);
  const defaultCaseForm = { patientId: '', primaryDiagnosis: '', prognosis: '', goalsOfCare: '', preferredPlaceOfCare: '', dnrStatus: false, dnrNotes: '', primaryCaregiverName: '', primaryCaregiverPhone: '', status: 'ACTIVE' };
  const [caseForm, setCaseForm] = useState<any>(defaultCaseForm);

  const [showSymptom, setShowSymptom] = useState(false);
  const defaultSymptomForm = { assessmentDate: new Date().toISOString().slice(0, 16), painScore: '', painLocation: '', symptoms: { nausea: '', fatigue: '', breathlessness: '', anxiety: '', constipation: '' }, notes: '' };
  const [symptomForm, setSymptomForm] = useState<any>(defaultSymptomForm);

  const [showPlan, setShowPlan] = useState(false);
  const defaultPlanForm = { goals: '', interventions: '', reviewDate: '', status: 'ACTIVE' };
  const [planForm, setPlanForm] = useState<any>(defaultPlanForm);

  const loadStats = useCallback(async () => {
    try { const r = await api.getPalliativeStats(); setStats(r?.data || {}); } catch { }
  }, []);
  useEffect(() => { loadStats(); }, [loadStats]);

  const loadCase = useCallback(async (patientId: string) => {
    setLoading(true);
    try {
      const r = await api.getPalliativeCaseByPatient(patientId);
      setKase(r?.data || null);
    } catch { setKase(null); }
    setLoading(false);
  }, []);

  const loadCaseFull = useCallback(async (caseId: string) => {
    try {
      const r = await api.getPalliativeCaseFull(caseId);
      const full = r?.data || {};
      setSymptoms(full.symptomHistory ? [...full.symptomHistory].reverse() : []);
      setPlan(full.plan || null);
    } catch { setSymptoms([]); setPlan(null); }
  }, []);

  useEffect(() => {
    if (patient) { loadCase(patient.id); } else { setKase(null); setSymptoms([]); setPlan(null); }
  }, [patient, loadCase]);

  useEffect(() => {
    if (kase) { loadCaseFull(kase.id); }
  }, [kase, loadCaseFull]);

  const saveCase = async () => {
    if (!patient) { notify('⚠️ Select a patient first'); return; }
    if (!caseForm.primaryDiagnosis) { notify('⚠️ Primary diagnosis required'); return; }
    setSaving(true);
    try {
      await api.createPalliativeCase({ ...caseForm, patientId: patient.id, doctorId: user?.id });
      notify('✅ Palliative case created!'); setShowCase(false); setCaseForm(defaultCaseForm);
      loadCase(patient.id); loadStats();
    } catch (e: any) { notify('❌ ' + (e.response?.data?.message || 'Error')); }
    setSaving(false);
  };

  const saveSymptom = async () => {
    if (!kase) return;
    setSaving(true);
    try {
      const cleanSymptoms = Object.fromEntries(Object.entries(symptomForm.symptoms).filter(([, v]) => v !== ''));
      await api.createSymptomAssessment({ ...symptomForm, symptoms: cleanSymptoms, palliativeCaseId: kase.id, patientId: patient.id, assessedById: user?.id });
      notify('✅ Symptom assessment recorded!'); setShowSymptom(false); setSymptomForm(defaultSymptomForm);
      loadCaseFull(kase.id); loadStats();
    } catch (e: any) { notify('❌ ' + (e.response?.data?.message || 'Error')); }
    setSaving(false);
  };

  const savePlan = async () => {
    if (!kase) return;
    setSaving(true);
    try {
      await api.savePalliativePlan({ ...planForm, palliativeCaseId: kase.id, patientId: patient.id });
      notify('✅ Care plan saved!'); setShowPlan(false);
      loadCaseFull(kase.id);
    } catch (e: any) { notify('❌ ' + (e.response?.data?.message || 'Error')); }
    setSaving(false);
  };

  const latestSymptom = symptoms[0];

  return (
    <Layout title="Palliative Care" actions={
      <div className="flex gap-2">
        {kase && <button onClick={() => { setPlanForm({ ...defaultPlanForm, ...(plan || {}) }); setShowPlan(true); }} className="btn-secondary text-sm">🎯 Care Plan</button>}
        {kase && <button onClick={() => { setSymptomForm(defaultSymptomForm); setShowSymptom(true); }} className="btn-secondary text-sm">📊 Symptom Assessment</button>}
        {!kase && patient && <button onClick={() => { setCaseForm(defaultCaseForm); setShowCase(true); }} className="btn-primary text-sm">+ Enroll in Palliative Care</button>}
      </div>
    }>
      {msg && <div className="fixed top-4 right-4 bg-white border shadow-xl rounded-xl px-4 py-3 text-sm z-50 max-w-sm">{msg}</div>}

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
        {[['Active Cases', stats.activeCases ?? '—', 'purple'], ['High Pain Today (≥7)', stats.highPainToday ?? '—', 'red'], ['Reviews This Week', stats.reviewsThisWeek ?? '—', 'blue']].map(([l, v, c]) => (
          <div key={String(l)} className="card p-3 text-center"><p className="text-xs text-slate-400">{l}</p><p className={`text-2xl font-bold text-${c}-600`}>{v}</p></div>
        ))}
      </div>

      <div className="card p-3 mb-4">
        <PatientSearchInput label="Patient" selected={patient} onSelect={setPatient} onClear={() => setPatient(null)} />
      </div>

      {!patient ? (
        <div className="card p-12 text-center text-slate-400"><span className="text-4xl block mb-2">🕊️</span>Select a patient above to view or start a palliative care case</div>
      ) : loading ? (
        <div className="card p-12 text-center text-slate-400">Loading...</div>
      ) : !kase ? (
        <div className="card p-12 text-center">
          <span className="text-4xl block mb-2">🕊️</span>
          <p className="text-slate-500 mb-3">No palliative care case for this patient yet</p>
          <button onClick={() => { setCaseForm(defaultCaseForm); setShowCase(true); }} className="btn-primary text-sm">+ Enroll in Palliative Care</button>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="card p-3 bg-gradient-to-r from-[#02357A] to-indigo-800 text-white">
            <div className="flex justify-between items-start">
              <div>
                <p className="font-bold text-lg">{patient.firstName || patient.first_name} {patient.lastName || patient.last_name}</p>
                <p className="text-blue-200 text-sm mt-1">{kase.primary_diagnosis || kase.primaryDiagnosis}</p>
                <p className="text-blue-200 text-xs mt-1">Enrolled: {fmtDate(kase.date_enrolled || kase.dateEnrolled || kase.created_at)}</p>
              </div>
              <div className="flex flex-col items-end gap-1">
                <Badge label={kase.status} className="bg-white/20 text-white text-xs" />
                {(kase.dnr_status || kase.dnrStatus) && <Badge label="DNR" className="bg-red-500/80 text-white text-xs" />}
                <FileShaClaimButton
                  patientId={patient.id}
                  patientName={`${patient.firstName || patient.first_name || ''} ${patient.lastName || patient.last_name || ''}`.trim()}
                  patientCrId={patient.sha_beneficiary_cr_id || patient.shaBeneficiaryCrId}
                  encounterId={kase.id}
                  serviceName="Palliative care"
                  from="palliative"
                  className="bg-white/20 hover:bg-white/30 text-xs px-2 py-1 rounded transition-colors text-white"
                />
              </div>
            </div>
            {(kase.goals_of_care || kase.goalsOfCare) && <p className="text-blue-100 text-sm mt-2 italic">"{kase.goals_of_care || kase.goalsOfCare}"</p>}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="card p-3">
              <h3 className="font-semibold text-sm mb-2">🏠 Care Preferences</h3>
              <p className="text-xs text-slate-500">Preferred Place of Care</p>
              <p className="text-sm mb-2">{kase.preferred_place_of_care || kase.preferredPlaceOfCare || '—'}</p>
              {(kase.dnr_notes || kase.dnrNotes) && <><p className="text-xs text-slate-500">DNR Notes</p><p className="text-sm">{kase.dnr_notes || kase.dnrNotes}</p></>}
            </div>
            <div className="card p-3">
              <h3 className="font-semibold text-sm mb-2">👤 Primary Caregiver</h3>
              <p className="text-sm">{kase.primary_caregiver_name || kase.primaryCaregiverName || '—'}</p>
              <p className="text-xs text-slate-500">{kase.primary_caregiver_phone || kase.primaryCaregiverPhone || ''}</p>
            </div>
          </div>

          <div className="card p-3">
            <div className="flex justify-between items-center mb-2">
              <h3 className="font-semibold text-sm">📊 Symptom Trend ({symptoms.length} assessments)</h3>
              {latestSymptom && <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${(latestSymptom.pain_score || latestSymptom.painScore) >= 7 ? 'bg-red-100 text-red-700' : (latestSymptom.pain_score || latestSymptom.painScore) >= 4 ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'}`}>Latest Pain: {latestSymptom.pain_score || latestSymptom.painScore}/10</span>}
            </div>
            {symptoms.length === 0 ? <p className="text-sm text-slate-400">No symptom assessments recorded</p> :
              <table className="w-full text-xs text-center">
                <thead className="bg-slate-50"><tr><th className="p-1.5">{t('common.date','Date')}</th><th>Pain</th><th>{t('common.location','Location')}</th><th>Other Symptoms</th><th>{t('common.notes','Notes')}</th></tr></thead>
                <tbody>
                  {symptoms.map((s: any, i: number) => (
                    <tr key={s.id || i} className="border-t">
                      <td className="p-1.5">{fmtDateTime(s.assessment_date || s.assessmentDate)}</td>
                      <td className={`font-bold ${s.pain_score >= 7 ? 'text-red-600' : s.pain_score >= 4 ? 'text-amber-600' : 'text-green-600'}`}>{s.pain_score ?? s.painScore ?? '—'}</td>
                      <td>{s.pain_location || s.painLocation || '—'}</td>
                      <td className="text-left">{s.symptoms ? Object.entries(s.symptoms).map(([k, v]) => `${k}:${v}`).join(', ') : '—'}</td>
                      <td className="text-left">{s.notes || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>}
          </div>

          {plan && (
            <div className="card p-3">
              <div className="flex justify-between items-center mb-2"><h3 className="font-semibold text-sm">🎯 Care Plan</h3><button onClick={() => { setPlanForm({ ...defaultPlanForm, ...plan }); setShowPlan(true); }} className="text-xs text-[#02357A] hover:underline">{t('common.edit','Edit')}</button></div>
              <p className="text-sm">{plan.goals}</p>
              {plan.interventions && <p className="text-xs text-slate-500 mt-1">{plan.interventions}</p>}
              {(plan.review_date || plan.reviewDate) && <p className="text-xs text-slate-400 mt-1">Review: {fmtDate(plan.review_date || plan.reviewDate)}</p>}
            </div>
          )}
        </div>
      )}

      <SlidePanel isOpen={showCase} onClose={() => setShowCase(false)} title="Enroll in Palliative Care" icon="🕊️" size="lg"
        actions={<button onClick={saveCase} disabled={saving} className="btn-primary w-full">{saving ? 'Saving...' : 'Create Case'}</button>}>
        <div className="space-y-3">
          {patient && <div className="bg-blue-50 border border-blue-200 rounded-lg p-2.5 text-sm text-[#02357A]">Patient: <strong>{patient.firstName || patient.first_name} {patient.lastName || patient.last_name}</strong></div>}
          <div><label className="label">Primary Diagnosis *</label><Textarea rows={2} value={caseForm.primaryDiagnosis} onChange={e => setCaseForm({ ...caseForm, primaryDiagnosis: e.target.value })} /></div>
          <div><label className="label">Prognosis</label><Input value={caseForm.prognosis} onChange={e => setCaseForm({ ...caseForm, prognosis: e.target.value })} /></div>
          <div><label className="label">Goals of Care</label><Textarea rows={2} value={caseForm.goalsOfCare} onChange={e => setCaseForm({ ...caseForm, goalsOfCare: e.target.value })} placeholder="e.g. Comfort-focused, symptom control" /></div>
          <div><label className="label">Preferred Place of Care</label>
            <Select value={caseForm.preferredPlaceOfCare} onChange={e => setCaseForm({ ...caseForm, preferredPlaceOfCare: e.target.value })}>
              <option value="">Select...</option>
              {['HOME', 'HOSPICE', 'HOSPITAL', 'NURSING_FACILITY'].map(p => <option key={p}>{p}</option>)}
            </Select>
          </div>
          <div className="flex items-center gap-2"><input type="checkbox" checked={caseForm.dnrStatus} onChange={e => setCaseForm({ ...caseForm, dnrStatus: e.target.checked })} /><label className="text-sm">DNR (Do Not Resuscitate) Status</label></div>
          {caseForm.dnrStatus && <div><label className="label">DNR Notes</label><Input value={caseForm.dnrNotes} onChange={e => setCaseForm({ ...caseForm, dnrNotes: e.target.value })} /></div>}
          <div className="grid grid-cols-2 gap-3">
            <div><label className="label">Primary Caregiver Name</label><Input value={caseForm.primaryCaregiverName} onChange={e => setCaseForm({ ...caseForm, primaryCaregiverName: e.target.value })} /></div>
            <div><label className="label">Primary Caregiver Phone</label><Input value={caseForm.primaryCaregiverPhone} onChange={e => setCaseForm({ ...caseForm, primaryCaregiverPhone: e.target.value })} /></div>
          </div>
        </div>
      </SlidePanel>

      <SlidePanel isOpen={showSymptom} onClose={() => setShowSymptom(false)} title="Symptom Assessment" icon="📊"
        actions={<button onClick={saveSymptom} disabled={saving} className="btn-primary w-full">{saving ? 'Saving...' : 'Save Assessment'}</button>}>
        <div className="space-y-3">
          <div><label className="label">Assessment Date/Time</label><Input type="datetime-local" value={symptomForm.assessmentDate} onChange={e => setSymptomForm({ ...symptomForm, assessmentDate: e.target.value })} /></div>
          <div><label className="label">Pain Score (0-10)</label><Input type="number" min="0" max="10" value={symptomForm.painScore} onChange={e => setSymptomForm({ ...symptomForm, painScore: e.target.value })} /></div>
          <div><label className="label">Pain Location</label><Input value={symptomForm.painLocation} onChange={e => setSymptomForm({ ...symptomForm, painLocation: e.target.value })} /></div>
          <div className="bg-slate-50 rounded-xl p-3">
            <p className="text-xs font-semibold text-slate-600 uppercase mb-2">Other Symptoms (0-3 severity)</p>
            <div className="grid grid-cols-2 gap-2">
              {Object.keys(symptomForm.symptoms).map(k => (
                <div key={k}><label className="label text-xs capitalize">{k}</label>
                  <Select value={symptomForm.symptoms[k]} onChange={e => setSymptomForm({ ...symptomForm, symptoms: { ...symptomForm.symptoms, [k]: e.target.value } })}>
                    <option value="">—</option><option value="0">0 - None</option><option value="1">1 - Mild</option><option value="2">2 - Moderate</option><option value="3">3 - Severe</option>
                  </Select>
                </div>
              ))}
            </div>
          </div>
          <div><label className="label">{t('common.notes','Notes')}</label><Textarea rows={2} value={symptomForm.notes} onChange={e => setSymptomForm({ ...symptomForm, notes: e.target.value })} /></div>
        </div>
      </SlidePanel>

      <SlidePanel isOpen={showPlan} onClose={() => setShowPlan(false)} title="Palliative Care Plan" icon="🎯"
        actions={<button onClick={savePlan} disabled={saving} className="btn-primary w-full">{saving ? 'Saving...' : 'Save Care Plan'}</button>}>
        <div className="space-y-3">
          <div><label className="label">Goals</label><Textarea rows={3} value={planForm.goals} onChange={e => setPlanForm({ ...planForm, goals: e.target.value })} /></div>
          <div><label className="label">Interventions</label><Textarea rows={3} value={planForm.interventions} onChange={e => setPlanForm({ ...planForm, interventions: e.target.value })} placeholder="e.g. Morphine titration, positioning, family support" /></div>
          <div><label className="label">Review Date</label><Input type="date" value={planForm.reviewDate} onChange={e => setPlanForm({ ...planForm, reviewDate: e.target.value })} /></div>
          <div><label className="label">{t('common.status','Status')}</label>
            <Select value={planForm.status} onChange={e => setPlanForm({ ...planForm, status: e.target.value })}>
              {['ACTIVE', 'COMPLETED', 'DISCONTINUED'].map(s => <option key={s}>{s}</option>)}
            </Select>
          </div>
        </div>
      </SlidePanel>
    </Layout>
  );
}
